/*
 * What the service worker (`worker.ts`) and the pages agree on. Pure code, no browser globals: the worker imports it, and so
 * do the app and its tests. docs/modules/notifications.md "Web push" and "Installable app".
 */

/** Messages between the worker and the pages (`postMessage` payloads have a `type`). */
export const SwMessage = {
  /** Page → worker: activate the waiting worker now (the user chose "Update"). */
  skipWaiting: 'orbita:skip-waiting',
  /** Worker → pages: a push message arrived (`payload`); the pages refresh their notification lists. */
  push: 'orbita:push',
  /** Worker → page: open `url` (an app path) inside the running app; the page answers on the transferred port. */
  navigate: 'orbita:navigate',
  /** Worker → pages: the browser replaced or dropped the push subscription; register it again. */
  subscriptionChanged: 'orbita:push-subscription-changed',
} as const;

/** The page shown for a navigation while the portal cannot be reached (public/offline.html, precached). */
export const OFFLINE_URL = '/offline.html';

/** Where a notification without a usable link leads. */
export const FALLBACK_PATH = '/notifications';

/** Prefix of every cache of the shell; the rest of the name is the build's version. */
export const CACHE_PREFIX = 'orbita-shell-';

export type PushLanguage = 'ru' | 'en' | 'uk';

/** The push payload the server builds (backend `PushPayload`): the least a notification needs. */
export interface PushPayload {
  v: 1;
  id: string;
  title: string;
  body: string;
  /** An app path (`/tasks?panel=task:42`). */
  url: string;
  /** Notifications with one tag replace each other on the device. */
  tag: string;
  /** When the event happened, milliseconds since 1970. */
  ts: number;
  /** Events of a grouped notification. */
  count: number;
  lang: PushLanguage;
}

const LANGUAGES: readonly string[] = ['ru', 'en', 'uk'];

const GENERIC_BODY: Record<PushLanguage, string> = {
  ru: 'У вас новое уведомление',
  en: 'You have a new notification',
  uk: 'У вас нове сповіщення',
};

function isLanguage(value: unknown): value is PushLanguage {
  return typeof value === 'string' && LANGUAGES.includes(value);
}

/** The language of the browser for texts the worker writes itself (a push it cannot read). */
export function languageOf(navigatorLanguage: string | undefined): PushLanguage {
  const prefix = (navigatorLanguage ?? '').toLowerCase().split('-')[0];
  return isLanguage(prefix) ? prefix : 'ru';
}

/**
 * The payload of a push message, or null when it is not one we understand (another version, garbage). The worker then still
 * shows a generic notification: a browser expects every push to show something.
 */
export function parsePushPayload(raw: unknown): PushPayload | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (value.v !== 1) return null;
  const { id, title, body, url, tag, ts, count, lang } = value;
  if (typeof id !== 'string' || typeof title !== 'string' || typeof body !== 'string') return null;
  if (typeof url !== 'string' || typeof tag !== 'string' || typeof ts !== 'number') return null;
  return {
    v: 1,
    id,
    title: title.slice(0, 200),
    body: body.slice(0, 500),
    url,
    tag: tag.slice(0, 100),
    ts,
    count: typeof count === 'number' && count > 0 ? Math.floor(count) : 1,
    lang: isLanguage(lang) ? lang : 'ru',
  };
}

/** What is shown for a push that could not be read. */
export function genericPayload(language: PushLanguage, now: number): PushPayload {
  return {
    v: 1,
    id: 'generic',
    title: 'Orbita',
    body: GENERIC_BODY[language],
    url: FALLBACK_PATH,
    tag: 'orbita-generic',
    ts: now,
    count: 1,
    lang: language,
  };
}

/**
 * The path (with query and hash) a notification opens: only a page of this origin — a link to another site, a protocol-relative
 * address or `javascript:` falls back to the notification list.
 */
export function resolvePath(url: unknown, origin: string): string {
  if (typeof url !== 'string' || url.length === 0 || url.length > 2048) return FALLBACK_PATH;
  try {
    const target = new URL(url, origin);
    if (target.origin !== origin) return FALLBACK_PATH;
    return target.pathname + target.search + target.hash;
  } catch {
    return FALLBACK_PATH;
  }
}

/** `NotificationOptions` plus the members browsers support but TypeScript's DOM library does not list. */
export interface NotificationDisplayOptions {
  body: string;
  tag: string;
  icon: string;
  badge: string;
  lang: string;
  timestamp: number;
  renotify: boolean;
  requireInteraction: boolean;
  data: { url: string; id: string };
}

/**
 * How a push is shown. Notifications with one tag replace each other, silently (`renotify: false`): a burst of events of one
 * notification updates the text on the device instead of ringing again. The link travels in `data`.
 */
export function notificationOptions(payload: PushPayload, origin: string): NotificationDisplayOptions {
  return {
    body: payload.body,
    tag: payload.tag,
    icon: '/icons/icon-192.png',
    badge: '/icons/badge-96.png',
    lang: payload.lang,
    timestamp: payload.ts,
    renotify: false,
    requireInteraction: false,
    data: { url: resolvePath(payload.url, origin), id: payload.id },
  };
}

/** The link kept in a notification's `data` (the worker wrote it; anything else opens the notification list). */
export function linkOf(data: unknown): string {
  if (typeof data === 'object' && data !== null && typeof (data as { url?: unknown }).url === 'string') {
    return (data as { url: string }).url;
  }
  return FALLBACK_PATH;
}

/** Addresses the worker never touches: the API and its streams, the public form and widget bundles, health, the worker itself. */
const BYPASS_PATHS = [
  '/api',
  '/hubs',
  '/openapi',
  '/scalar',
  '/forms',
  '/livechat',
  '/healthz',
  '/sw.js',
  '/mockServiceWorker.js',
];

export type RequestRoute =
  /** A page load: the network, and the offline page when there is none. */
  | 'navigate'
  /** A file of the precached shell (icons, offline page, boot script): the cache. */
  | 'precached'
  /** A hashed build asset (`/assets/…`): the cache, filled on first use. */
  | 'asset'
  /** Everything else — the API above all: the worker stays out of it, the browser's own network stack answers. */
  | 'bypass';

export interface RequestFacts {
  method: string;
  /** `Request.mode`. */
  mode: string;
  /** Absolute URL. */
  url: string;
  /** The request carries a `Range` header (media, large downloads). */
  hasRange: boolean;
}

function isBypassPath(path: string): boolean {
  return BYPASS_PATHS.some((prefix) => path === prefix || path.startsWith(prefix + '/'));
}

/**
 * What the worker does with a request. It only ever serves GET requests of its own origin, never an API response — nothing
 * authenticated is stored or answered from a cache.
 */
export function routeRequest(request: RequestFacts, origin: string, precached: ReadonlySet<string>): RequestRoute {
  if (request.method !== 'GET' || request.hasRange) return 'bypass';
  let url: URL;
  try {
    url = new URL(request.url);
  } catch {
    return 'bypass';
  }
  if (url.origin !== origin || isBypassPath(url.pathname)) return 'bypass';
  if (request.mode === 'navigate') return 'navigate';
  if (precached.has(url.pathname)) return 'precached';
  if (url.pathname.startsWith('/assets/')) return 'asset';
  return 'bypass';
}
